/**
 * Canonical definition of every input the Form Generator can render.
 *
 * This list is the single source of truth for three consumers that used to keep
 * their own copies and drift apart:
 *   - the public form (`form-generator-public/FormGeneratorPublicPage`)
 *   - the form builder (`settings/form-generator/FormGeneratorForm`)
 *   - the email template variable menu (`settings/templates/TemplateForm`)
 *
 * The `key` doubles as the payload key sent on submit and as the `{{variable}}`
 * name available to email templates, so renaming one silently breaks templates.
 */

import { DEFAULT_POSITIONS } from './positions';

export const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming'
];

/**
 * Re-exported from the profile's own list rather than restated here: what an
 * onboarding form collects lands on `position`, and the two carried different
 * options, so a new hire picked something the profile could not offer back.
 *
 * The real list is now kept by the application and reaches the public form with the
 * form itself. This is only what that page starts on, and falls back to.
 */
export const POSITION_OPTIONS = DEFAULT_POSITIONS;

export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Ordered so the builder and the public form present fields consistently.
 * `isEmailField` marks the two addresses that can be picked as the form's
 * "Email Field" — they are offered in a separate select, never as extra inputs.
 */
export const FORM_FIELDS = [
  { key: 'firstName', label: 'Legal Name', type: 'text' },
  { key: 'legalMiddleName', label: 'Legal Middle Name', type: 'text' },
  { key: 'lastName', label: 'Legal Last Name', type: 'text' },
  { key: 'preferredFirstName', label: 'Preferred Business Name', type: 'text' },
  { key: 'preferredMiddleName', label: 'Preferred Business Middle Name', type: 'text' },
  { key: 'preferredLastName', label: 'Preferred Business Last Name', type: 'text' },
  { key: 'birthday', label: 'Birthdate', type: 'date' },
  { key: 'phoneNumber', label: 'Personal Phone Number', type: 'phone' },
  { key: 'businessPhoneNumber', label: 'Business Phone Number', type: 'phone' },
  { key: 'streetAddress', label: 'Address Line 1', type: 'text' },
  { key: 'streetAddress2', label: 'Address Line 2', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State', type: 'state' },
  { key: 'zipcode', label: 'Zipcode', type: 'text' },
  { key: 'emergencyContactName', label: 'Emergency Contact Name', type: 'text' },
  { key: 'emergencyContactPhone', label: 'Emergency Contact Phone', type: 'phone' },
  { key: 'startingDate', label: 'Starting Date', type: 'date' },
  { key: 'todaysDate', label: "Today's Date", type: 'date' },
  { key: 'position', label: 'Position', type: 'position' },
  { key: 'nmlsId', label: 'NMLS ID', type: 'text' },
  { key: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com' },
  {
    key: 'headshot',
    label: 'Upload Your Headshot',
    type: 'file',
    accept: 'image/*',
    description: 'Please upload a high-quality, professional photo:',
    requirements: [
      'Neutral background',
      'Business attire',
      'Clear and well-lit',
      'Friendly, approachable expression',
      'No filters or casual selfies'
    ]
  },
  {
    // Written in the form rather than uploaded: it lands on the profile's own
    // `professionalBio` text field, which is what the Marketing tab edits.
    key: 'professionalBio',
    label: 'Your Professional Bio',
    type: 'textarea',
    maxLength: 5000,
    description: 'Please write a short bio that includes:',
    requirements: ['A brief intro', 'Relevant experience', 'Key skills or certifications', 'Any career highlights']
  },
  // Same keys the profile's Marketing tab writes to, so what a new hire reports
  // here lands on their profile untouched by `createUser`.
  { key: 'linkedIn', label: 'LinkedIn', type: 'url', placeholder: 'https://linkedin.com/in/username' },
  { key: 'facebook', label: 'Facebook', type: 'url', placeholder: 'https://facebook.com/username' },
  { key: 'instagram', label: 'Instagram', type: 'url', placeholder: 'https://instagram.com/username' },
  { key: 'twitter', label: 'X (Twitter)', type: 'url', placeholder: 'https://x.com/username' },
  { key: 'youtube', label: 'YouTube', type: 'url', placeholder: 'https://youtube.com/@username' },
  { key: 'tiktok', label: 'TikTok', type: 'url', placeholder: 'https://tiktok.com/@username' },
  { key: 'iloanEmail', label: 'iLoan Email Address', type: 'email', isEmailField: true },
  { key: 'email', label: 'Personal Email Address', type: 'email', isEmailField: true }
];

/** Keyed lookup used while rendering the public form. */
export const FIELD_CONFIG = FORM_FIELDS.reduce((config, field) => {
  config[field.key] = field;
  return config;
}, {});

/** Addresses selectable as the form's "Email Field". */
export const EMAIL_FIELD_OPTIONS = FORM_FIELDS.filter(field => field.isEmailField).map(field => ({
  value: field.key,
  label: field.label
}));

/** Everything else, offered as additional inputs in the builder. */
export const ALL_FIELD_OPTIONS = FORM_FIELDS.filter(field => !field.isEmailField).map(field => ({
  value: field.key,
  label: field.label
}));

/** File inputs are stored as attachments, so templates only ever get their filename. */
export const FILE_FIELD_KEYS = FORM_FIELDS.filter(field => field.type === 'file').map(field => field.key);

/** Masked inputs: the value only counts as filled once all ten digits are there. */
export const PHONE_FIELD_KEYS = FORM_FIELDS.filter(field => field.type === 'phone').map(field => field.key);

/** Digits only, so a masked value can be measured whatever separators it carries. */
export const phoneDigits = value => String(value || '').replace(/\D/g, '');

export const isCompletePhone = value => phoneDigits(value).length === 10;

// The mask only formats what it displays, so stored numbers need this to converge on it.
export const formatUsPhone = value => {
  const digits = phoneDigits(value);
  // Eleven digits means a country code; the mask would read it as the area code.
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return String(value || '');
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

/**
 * Variables offered by the email template editor. Every form field is available,
 * including the two email addresses, because a template may want to greet the
 * submitter or echo back the address the form was sent to.
 */
export const TEMPLATE_VARIABLES = FORM_FIELDS.map(field => ({
  label: FILE_FIELD_KEYS.includes(field.key) ? `${field.label} (file name)` : field.label,
  value: `{{${field.key}}}`
}));

/**
 * Side effect a form runs on submit, on top of always storing the submission.
 * Mirrors the `submissionAction` enum on the API's FormGenerator schema.
 */
export const SUBMISSION_ACTION_OPTIONS = [
  { value: 'none', label: 'Store the submission only' },
  { value: 'createUser', label: 'Create a platform user' }
];

/**
 * Required by the User schema on the API. A form that creates users but does
 * not collect these produces a submission the action can only fail on, so the
 * builder blocks that combination up front.
 *
 * `iloanEmail` is deliberately absent. Onboarding forms are filled in by people
 * who do not have an iLoan mailbox yet, so they cannot report the address — an
 * admin assigns it on the profile before sending the invitation.
 */
export const CREATE_USER_REQUIRED_FIELDS = ['firstName', 'lastName', 'email'];

/** Which required fields a form is missing, given its configuration. */
export const missingCreateUserFields = form => {
  const configured = [...(form.emailField ? [form.emailField] : []), ...(form.additionalInputs || [])];
  return CREATE_USER_REQUIRED_FIELDS.filter(key => !configured.includes(key));
};

/**
 * Whether a configured input must be filled in before the form can be submitted.
 *
 * Inputs are required unless the builder listed them in `optionalInputs`. Storing
 * the exception rather than the rule is what lets forms built before this existed
 * keep behaving exactly as they did, with every field required.
 *
 * Two cases override the checkbox, because letting them through would only
 * produce a submission the form cannot act on:
 *   - the Email Field, which is the address the form is delivered to
 *   - the names a `createUser` form has to pass to the User schema
 */
export const isFieldRequired = (form, key) => {
  if (!form) return true;
  if (key && key === form.emailField) return true;
  if (form.submissionAction === 'createUser' && CREATE_USER_REQUIRED_FIELDS.includes(key)) return true;
  return !(form.optionalInputs || []).includes(key);
};
